import { type RefObject, useEffect, useRef, useState } from "react";

import {
  BaselineSeries,
  ColorType,
  CrosshairMode,
  createChart,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  LineSeries,
  LineStyle,
  type MouseEventParams,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";

import type { AtdTheme } from "~/v3-poc/features/user-preferences";
import type {
  PortfolioComparisonChartSeries,
  PortfolioHistoryChartPoint,
} from "./portfolioHistory";
import { interpolatePortfolioHistoryValue } from "./portfolioHistory";
import type { PortfolioLookback } from "./portfolioHistoryTimeSettings";

export interface UsePortfolioHistoryChartLifecycleControllerParams {
  comparisons: PortfolioComparisonChartSeries[];
  data: PortfolioHistoryChartPoint[];
  isComparisonFetching: boolean;
  isPortfolioFetching: boolean;
  lookback: PortfolioLookback;
  percentageData: PortfolioHistoryChartPoint[];
  shouldRenderChart: boolean;
  theme: AtdTheme;
}

export interface PortfolioHistoryChartLifecycleControllerValue {
  chartContainerRef: RefObject<HTMLDivElement>;
}

const readColor = (styles: CSSStyleDeclaration, property: string) =>
  styles.getPropertyValue(property).trim();

const toSeriesData = (data: readonly PortfolioHistoryChartPoint[]) =>
  data.map(({ time, value }) => ({ time: time as UTCTimestamp, value }));

const getPortfolioSeriesColors = (styles: CSSStyleDeclaration) => ({
  topLineColor: readColor(styles, "--yellow-9"),
  topFillColor1: readColor(styles, "--yellow-a5"),
  bottomLineColor: readColor(styles, "--yellow-9"),
  bottomFillColor1: readColor(styles, "--yellow-a5"),
});

const formatPortfolioValue = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);

const formatPercentageValue = (value: number) =>
  `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;

const formatPortfolioHistoryDate = (time: Time) => {
  const date =
    typeof time === "number"
      ? new Date(time * 1_000)
      : typeof time === "string"
        ? new Date(`${time}T00:00:00Z`)
        : new Date(Date.UTC(time.year, time.month - 1, time.day));

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const getPortfolioPriceRange = (
  data: readonly PortfolioHistoryChartPoint[],
) => {
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;

  for (const { value } of data) {
    minValue = Math.min(minValue, value);
    maxValue = Math.max(maxValue, value);
  }

  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    return null;
  }
  if (minValue === maxValue) {
    const padding = Math.abs(minValue) * 0.01 || 1;
    return { minValue: minValue - padding, maxValue: maxValue + padding };
  }
  return { minValue, maxValue };
};

interface ComparisonSeriesEntry {
  color: PortfolioComparisonChartSeries["color"];
  data: readonly PortfolioHistoryChartPoint[];
  hoverPriceLine: IPriceLine;
  series: ISeriesApi<"Line">;
}

interface ChartRuntime {
  chart: IChartApi;
  comparisonSeries: Map<string, ComparisonSeriesEntry>;
  portfolioHoverPriceLine: IPriceLine;
  portfolioPercentageSeries: ISeriesApi<"Line">;
  portfolioSeries: ISeriesApi<"Baseline">;
}

export const usePortfolioHistoryChartLifecycleController = ({
  comparisons,
  data,
  isComparisonFetching,
  isPortfolioFetching,
  lookback,
  percentageData,
  shouldRenderChart,
  theme,
}: UsePortfolioHistoryChartLifecycleControllerParams): PortfolioHistoryChartLifecycleControllerValue => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [runtime, setRuntime] = useState<ChartRuntime | null>(null);
  const framedSelectionRef = useRef<string | null>(null);
  const isIntraday = lookback === "1D";

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!shouldRenderChart || !container) {
      return;
    }

    const styles = getComputedStyle(container);
    const chart = createChart(container, {
      autoSize: true,
      handleScroll: false,
      handleScale: false,
      layout: {
        attributionLogo: false,
        background: { type: ColorType.Solid, color: "transparent" },
        fontFamily: readColor(styles, "--default-font-family"),
        textColor: readColor(styles, "--gray-11"),
      },
      grid: {
        horzLines: { color: readColor(styles, "--gray-a5") },
        vertLines: { visible: false },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        horzLine: { labelVisible: false, style: LineStyle.Dashed },
        vertLine: { style: LineStyle.Dashed },
      },
      leftPriceScale: { visible: true, borderVisible: false },
      rightPriceScale: { visible: false, borderVisible: false },
      timeScale: {
        borderVisible: false,
        minBarSpacing: 0.01,
        timeVisible: true,
      },
    });
    const portfolioSeries = chart.addSeries(BaselineSeries, {
      baseValue: { type: "price", price: 0 },
      priceScaleId: "left",
      ...getPortfolioSeriesColors(styles),
      topFillColor2: "transparent",
      bottomFillColor2: "transparent",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat: {
        type: "custom",
        formatter: formatPortfolioValue,
      },
    });
    const portfolioPercentageSeries = chart.addSeries(LineSeries, {
      priceScaleId: "right",
      color: "transparent",
      lineWidth: 2,
      crosshairMarkerVisible: false,
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat: {
        type: "custom",
        formatter: formatPercentageValue,
      },
    });
    const portfolioHoverPriceLine = portfolioSeries.createPriceLine({
      axisLabelVisible: false,
      color: readColor(styles, "--yellow-9"),
      lineVisible: false,
      price: 0,
    });
    const comparisonSeries = new Map<string, ComparisonSeriesEntry>();
    const hideHoverPriceLines = () => {
      portfolioHoverPriceLine.applyOptions({ axisLabelVisible: false });
      comparisonSeries.forEach(({ hoverPriceLine }) => {
        hoverPriceLine.applyOptions({ axisLabelVisible: false });
      });
    };
    const handleCrosshairMove = (param: MouseEventParams) => {
      hideHoverPriceLines();
      const portfolioData = param.seriesData.get(portfolioSeries);
      if (portfolioData && "value" in portfolioData) {
        portfolioHoverPriceLine.applyOptions({
          axisLabelVisible: true,
          price: portfolioData.value,
        });
      }
      comparisonSeries.forEach(({ data, hoverPriceLine, series }) => {
        const comparisonData = param.seriesData.get(series);
        const value =
          comparisonData && "value" in comparisonData
            ? comparisonData.value
            : typeof param.time === "number"
              ? interpolatePortfolioHistoryValue(data, param.time)
              : undefined;
        if (value !== undefined) {
          hoverPriceLine.applyOptions({
            axisLabelVisible: true,
            price: value,
          });
        }
      });
    };
    chart.subscribeCrosshairMove(handleCrosshairMove);
    setRuntime({
      chart,
      comparisonSeries,
      portfolioHoverPriceLine,
      portfolioPercentageSeries,
      portfolioSeries,
    });

    return () => {
      framedSelectionRef.current = null;
      comparisonSeries.clear();
      setRuntime(null);
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.remove();
    };
  }, [shouldRenderChart]);

  useEffect(() => {
    runtime?.chart.applyOptions({
      localization: {
        timeFormatter: isIntraday ? undefined : formatPortfolioHistoryDate,
      },
      timeScale: { timeVisible: isIntraday },
    });
  }, [isIntraday, runtime]);

  useEffect(() => {
    if (!runtime) {
      return;
    }

    const priceRange = getPortfolioPriceRange(data);
    runtime.portfolioSeries.applyOptions({
      autoscaleInfoProvider: () => (priceRange ? { priceRange } : null),
      baseValue: {
        type: "price",
        price: priceRange?.minValue ?? 0,
      },
      priceFormat: {
        type: "custom",
        formatter: formatPortfolioValue,
      },
    });

    runtime.portfolioSeries.setData(toSeriesData(data));
  }, [data, runtime]);

  useEffect(() => {
    if (!runtime) {
      return;
    }

    runtime.portfolioPercentageSeries.setData(toSeriesData(percentageData));
  }, [percentageData, runtime]);

  useEffect(() => {
    runtime?.chart.applyOptions({
      rightPriceScale: {
        visible: comparisons.length > 0,
        borderVisible: false,
      },
    });
  }, [comparisons.length, runtime]);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!runtime || !container) {
      return;
    }

    if (comparisons.length === 0 && runtime.comparisonSeries.size === 0) {
      return;
    }

    const activeSymbols = new Set(comparisons.map(({ symbol }) => symbol));
    runtime.comparisonSeries.forEach(({ series }, symbol) => {
      if (!activeSymbols.has(symbol)) {
        runtime.chart.removeSeries(series);
        runtime.comparisonSeries.delete(symbol);
      }
    });

    const styles = getComputedStyle(container);
    comparisons.forEach(({ color, data: comparisonData, symbol }) => {
      let entry = runtime.comparisonSeries.get(symbol);
      if (!entry) {
        const colorValue = readColor(styles, `--${color}-9`);
        const series = runtime.chart.addSeries(LineSeries, {
          priceScaleId: "right",
          color: colorValue,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          priceFormat: {
            type: "custom",
            formatter: formatPercentageValue,
          },
        });
        entry = {
          color,
          data: comparisonData,
          hoverPriceLine: series.createPriceLine({
            axisLabelVisible: false,
            color: colorValue,
            lineVisible: false,
            price: 0,
            title: symbol,
          }),
          series,
        };
        runtime.comparisonSeries.set(symbol, entry);
      }
      entry.color = color;
      entry.data = comparisonData;
      entry.series.applyOptions({
        color: readColor(styles, `--${color}-9`),
      });
      entry.series.setData(toSeriesData(comparisonData));
    });
  }, [comparisons, runtime]);

  useEffect(() => {
    const chart = runtime?.chart;
    const firstPoint = data[0];
    const lastPoint = data.at(-1);
    const frameKey = `${lookback}:${firstPoint?.time ?? ""}:${lastPoint?.time ?? ""}:${comparisons
      .map(({ symbol }) => symbol)
      .sort()
      .join(",")}`;
    if (
      !chart ||
      isPortfolioFetching ||
      isComparisonFetching ||
      framedSelectionRef.current === frameKey
    ) {
      return;
    }

    if (firstPoint && lastPoint && firstPoint.time !== lastPoint.time) {
      chart.timeScale().setVisibleRange({
        from: firstPoint.time as UTCTimestamp,
        to: lastPoint.time as UTCTimestamp,
      });
    } else {
      chart.timeScale().fitContent();
    }
    framedSelectionRef.current = frameKey;
  }, [
    comparisons,
    data,
    isComparisonFetching,
    isPortfolioFetching,
    lookback,
    runtime,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: theme is the trigger; the effect re-reads inherited Radix CSS variables rather than the theme string.
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container || !runtime) {
      return;
    }

    const styles = getComputedStyle(container);
    runtime.chart.applyOptions({
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: readColor(styles, "--gray-11"),
      },
      grid: { horzLines: { color: readColor(styles, "--gray-a5") } },
    });
    runtime.portfolioSeries.applyOptions(getPortfolioSeriesColors(styles));
    runtime.portfolioHoverPriceLine.applyOptions({
      color: readColor(styles, "--yellow-9"),
    });
    runtime.comparisonSeries.forEach(({ color, hoverPriceLine, series }) => {
      const colorValue = readColor(styles, `--${color}-9`);
      hoverPriceLine.applyOptions({ color: colorValue });
      series.applyOptions({
        color: colorValue,
      });
    });
  }, [runtime, theme]);

  return { chartContainerRef };
};
